// Мок WebSocket-транспорта протокола поддержки.
// Имитирует контракт реального сокета:
//   connect()                      -> Promise (handshake + auth)
//   request(endpoint, payload)     -> Promise<{ ... }> (протокол request/response)
//   on(eventName, fn)              -> unsubscribe fn (события с сервера)
//
// Токен и история хранятся в localStorage, чтобы имитировать
// восстановление сессии после перезагрузки страницы.
// При появлении реального WS замените файл, на который смотрит supportApi.js.

export const TOKEN_KEY = "support_chat_token";

const MESSAGES_KEY = (token) => `support_chat_messages_${token}`;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const listeners = new Map(); // event_name -> Set<fn>

const OPERATOR_REPLIES = [
	"Здравствуйте! Спасибо за обращение, уже разбираемся. Ответим в ближайшие пару минут.",
	"Приняли! Уточню детали у методиста и вернусь с ответом.",
	"Понял вас! Если вопрос срочный — быстрее всего написать нам в Telegram, ссылка ниже в чате.",
	"Спасибо за сообщение! Оператор скоро подключится к диалогу.",
];

function loadMessages(token) {
	try {
		return JSON.parse(localStorage.getItem(MESSAGES_KEY(token))) ?? [];
	} catch {
		return [];
	}
}

function saveMessages(token, messages) {
	localStorage.setItem(MESSAGES_KEY(token), JSON.stringify(messages));
}

function nextId(messages) {
	return messages.reduce((max, m) => Math.max(max, Number(m.id) || 0), 0) + 1;
}

function emit(eventName, payload) {
	listeners.get(eventName)?.forEach((fn) => fn(payload));
}

// Тестовые рычаги (используются смоук-тестами)
export const mockControls = { failSend: false };

async function handleRequest(endpoint, payload) {
	// имитация сетевой задержки
	await sleep(150 + Math.random() * 250);

	if (endpoint === "send_message" && mockControls.failSend) {
		// eslint-disable-next-line no-throw-literal -- объект ошибки протокола { kind, details }
		throw { kind: "network", details: "mock network error" };
	}

	switch (endpoint) {
		case "auth": {
			const token = payload?.token || crypto.randomUUID();
			localStorage.setItem(TOKEN_KEY, token);
			return { token };
		}

		case "get_active_ticket": {
			const token = localStorage.getItem(TOKEN_KEY);
			const messages = token ? loadMessages(token) : [];
			if (!messages.length) return null;
			return {
				id: 1,
				status: "ACTIVE",
				create_datetime: messages[0].create_datetime,
			};
		}

		case "get_messages": {
			const token = localStorage.getItem(TOKEN_KEY);
			const all = token ? loadMessages(token) : [];
			const { offset = 0, limit = 20 } = payload ?? {};
			return {
				total_count: all.length,
				items: all.slice(offset, offset + limit),
			};
		}

		case "send_message": {
			const token = localStorage.getItem(TOKEN_KEY);
			const messages = loadMessages(token);
			const hadTicket = messages.length > 0;

			const message = {
				id: nextId(messages),
				text: payload.text,
				create_datetime: new Date().toISOString(),
				channel: "inbound",
				attachments: [],
			};
			messages.push(message);
			saveMessages(token, messages);

			// имитация ответа оператора событием "message"
			const replyText =
				OPERATOR_REPLIES[Math.floor(Math.random() * OPERATOR_REPLIES.length)];
			setTimeout(() => {
				const current = loadMessages(token);
				const reply = {
					id: nextId(current),
					text: replyText,
					create_datetime: new Date().toISOString(),
					channel: "outbound",
					attachments: [],
				};
				current.push(reply);
				saveMessages(token, current);
				emit("message", reply);
			}, 1200 + Math.random() * 800);

			return {
				message_id: message.id,
				ticket: hadTicket
					? null
					: {
							id: 1,
							status: "ACTIVE",
							create_datetime: messages[0].create_datetime,
						},
			};
		}

		default:
			// eslint-disable-next-line no-throw-literal -- объект ошибки протокола { kind, details }
			throw { kind: "unknown_endpoint", details: `Unknown endpoint: ${endpoint}` };
	}
}

export function connect() {
	// У реального сокета здесь будет new WebSocket(url) + auth-handshake.
	// Мок "подключён" всегда; сессия восстанавливается по токену из localStorage.
	return Promise.resolve();
}

export function request(endpoint, payload) {
	return handleRequest(endpoint, payload);
}

export function on(eventName, fn) {
	if (!listeners.has(eventName)) listeners.set(eventName, new Set());
	listeners.get(eventName).add(fn);
	return () => listeners.get(eventName)?.delete(fn);
}
